import { Box, FlexBox, H1, Typography, Visible, responsive, tokens, tokensRaw } from '@vp/swan';
import styled from 'styled-components';
import { Maybe } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { BottomBar, ProgressBar } from '@99designs/swan-wizard';
import { NormalisedBriefProduct, useBriefContext } from '../BriefContext';
import { HowItWorks } from '../HowItWorks/HowItWorks';
import { FieldGroupData } from './FieldGroup';
import { LogomakerAsset, StepProductCard, WorkEntityDisplay } from './StepProductCard';
import { SubmitMultiStepBriefButton } from './SubmitBriefButton';
import type { InvitedDesigner } from './InvitedDesigner';

const ResponsiveFlex = responsive(FlexBox);

const HowItWorksContainer = styled(ResponsiveFlex)`
    justify-content: center;
    align-items: flex-end;
    padding-top: ${tokens.SwanSemSpace10};
    margin-right: ${tokens.SwanSemSpace12};
    margin-left: ${tokens.SwanSemSpace5};

    // margin will adjust dynamically with the viewport
    @media (max-width: ${tokensRaw.SwanBaseBreakpointLgStart}) {
        margin-right: calc(
            ${tokens.SwanSemSpace12} - (${tokensRaw.SwanBaseBreakpointLgStart} - 100vw) / 3.5
        );
    }
`;

export const IntroStep = ({
    product,
    step,
    startingAsset,
    reportedIssues,
    group,
    NextAction,
    sizeErrorBanner,
    invitedDesigner,
}: {
    product: Maybe<NormalisedBriefProduct>;
    step: JSX.Element;
    group: FieldGroupData;
    startingAsset?: WorkEntityDisplay | LogomakerAsset;
    reportedIssues?: readonly string[] | null;
    NextAction?: React.ComponentType;
    sizeErrorBanner?: React.ReactNode;
    invitedDesigner?: InvitedDesigner;
}) => {
    const {
        config: {
            showSubtitle,
            subtitleCopy,
            showHowItWorks,
            showProductCard,
            experience,
            submitWithCart,
            submitButtonCopy,
            submitButtonEditCopy,
            tenant: { showProgressBar },
        },
        isPricingLoading,
    } = useBriefContext();
    const singleStep = experience === 'SINGLE_STEP';

    const subtitle =
        showSubtitle &&
        (subtitleCopy ? (
            <Typography
                mb={7}
                dangerouslySetInnerHTML={{ __html: subtitleCopy }}
                data-testid="brief-intro-step-subtitle"
            />
        ) : (
            <Typography mb={7} data-testid="brief-intro-step-subtitle">
                {__('Answer a few short questions to help us design the perfect solution')}
            </Typography>
        ));

    const howItWorksLargeScreen = showHowItWorks && (
        <Visible xl lg md>
            <HowItWorksContainer>
                <HowItWorks />
            </HowItWorksContainer>
        </Visible>
    );
    const howItWorksMobile = showHowItWorks && (
        <Visible sm xs paddingBottom={3}>
            <HowItWorks />
        </Visible>
    );

    const productCard = showProductCard && (
        <Box textAlign={'left'} paddingBottom={7}>
            {product && (
                <StepProductCard
                    startingAsset={startingAsset}
                    reportedIssues={reportedIssues}
                    product={product}
                    invitedDesigner={invitedDesigner}
                    isPricingLoading={isPricingLoading}
                />
            )}
        </Box>
    );

    return (
        <>
            <ResponsiveFlex
                data-testid="brief-intro-step"
                md={{ px: 8 }}
                display={'flex'}
                style={{ justifyContent: 'center', flex: 1 }}
            >
                {howItWorksLargeScreen}

                <ResponsiveFlex
                    data-testid="brief-intro-step-content"
                    justifyContent="center"
                    sm={{ py: 9 }}
                    xs={{ pb: 10, pt: 0 }}
                    style={{ maxWidth: '696px', flex: 1 }}
                >
                    <Box mb={4} textAlign="left" style={{ flex: 1 }}>
                        {!singleStep && showProgressBar && <ProgressBar />}

                        <Box mx={5} data-testid="brief-intro-step-header">
                            {sizeErrorBanner && <Box mb={5}>{sizeErrorBanner}</Box>}
                            <H1
                                fontSkin={'title-headline'}
                                mb={3}
                                data-testid="brief-intro-step-title"
                            >
                                {group.title}
                            </H1>
                            {subtitle}
                            {howItWorksMobile}
                            {productCard}
                        </Box>
                        {step}
                        {singleStep && (
                            <ResponsiveFlex
                                data-testid="brief-single-step-actions"
                                alignItems="center"
                                justifyContent="space-between"
                                paddingBottom={4}
                                paddingX={4}
                                sm={{ paddingX: 5 }}
                            >
                                <SubmitMultiStepBriefButton
                                    label={submitButtonCopy}
                                    editLabel={submitButtonEditCopy}
                                    isWithCart={submitWithCart}
                                    isEditMode={false}
                                    width="full-width"
                                    isDisabled={!!sizeErrorBanner}
                                />
                            </ResponsiveFlex>
                        )}
                    </Box>
                </ResponsiveFlex>
            </ResponsiveFlex>
            {!singleStep && <BottomBar NextAction={NextAction} />}
        </>
    );
};
